import { useState, useEffect } from 'react';
import { FieldValues } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import { useDebounce } from 'usehooks-ts';

import { SUBSCRIBE_URL } from '~/lib/util/constants';
import { isValidEmail } from '~/lib/util';
import useGtag from '~/lib/useGtag';

interface UseEmailSubscribeReturnProps {
  submitEmail: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  isEmailValid: boolean;
  isEmailSubmitted: boolean;
  hasEmailError: string;
  onChangeEmail: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFormChangeEmail: (d: FieldValues) => void;
  email: string;
}

interface FetchJSONBody {
  email_subscribed: boolean;
}

function createFormDataFromUTMParams(
  utm_source?: string,
  utm_campaign?: string,
  utm_medium?: string,
  componentName?: string,
): FormData {
  const formData = new FormData();

  if (utm_source) {
    formData.append('utm_source', utm_source);
  }

  if (utm_campaign) {
    formData.append('utm_campaign', utm_campaign);
  }

  if (utm_medium) {
    formData.append('utm_medium', utm_medium);
  }

  if (componentName) {
    formData.append('content', componentName);
  }

  return formData;
}

export function useEmailSubscribe(
  componentName?: string,
): UseEmailSubscribeReturnProps {
  const router = useRouter();
  const { gtag } = useGtag();
  const [cookies] = useCookies(['csrftoken']);
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isEmailSubmitted, setIsEmailSubmitted] = useState(false);
  const [hasEmailError, setHasEmailError] = useState('');
  const debouncedEmail = useDebounce(email, 500);

  useEffect(() => {
    setIsEmailSubmitted(false);
    setHasEmailError('');

    if (debouncedEmail) {
      setIsEmailValid(isValidEmail(debouncedEmail));
    } else {
      setIsEmailValid(true);
    }
  }, [debouncedEmail]);

  function onChangeEmail(e: React.ChangeEvent<HTMLInputElement>) {
    setEmail(e.target.value);
  }

  function onFormChangeEmail(d: FieldValues) {
    setEmail(d.email);
  }

  function submitEmail(): void {
    if (typeof window === 'undefined') return;

    if (!email || !isEmailValid) {
      setHasEmailError('Please enter a valid email address.');
      return;
    }

    const { utm_source, utm_campaign, utm_medium } = router.query;
    const formData = createFormDataFromUTMParams(
      utm_source as string,
      utm_campaign as string,
      utm_medium as string,
      componentName,
    );

    formData.append('email', email);

    const headers = new Headers();
    headers.append('X-CSRFToken', cookies?.csrftoken);

    const baseURL =
      (typeof window === 'undefined'
        ? process.env.DJANGO_INTERNAL_URL
        : process.env.NEXT_PUBLIC_DJANGO_PUBLIC_URL) || '';

    fetch(baseURL + SUBSCRIBE_URL, {
      credentials: 'include',
      method: 'POST',
      headers,
      body: formData,
    })
      .then(async (response: Response): Promise<FetchJSONBody> => {
        if (!response.ok) {
          throw new Error('Unable to submit your email, please try again.');
        }

        if (response.status === 205) {
          throw new Error(
            'The email address you entered used this coupon in a previous transaction. Limit one per email.',
          );
        }

        try {
          return await response.json();
        } catch (e) {
          throw new Error('Unable to validate email, please try again.');
        }
      })
      .then((body: FetchJSONBody) => {
        setIsEmailSubmitted(true);

        if (body?.email_subscribed) {
          gtag('event', 'submit_email', {
            content: formData.get('content') || '',
            email: formData.get('email') || '',
          });
        }
      })
      .catch((error: Error) => {
        setHasEmailError(`${error}`);
      });
  }

  return {
    email,
    hasEmailError,
    isEmailSubmitted,
    isEmailValid,
    onChangeEmail,
    onFormChangeEmail,
    submitEmail,
  };
}
